import jsPDF from "jspdf";

export interface AnalyzerScore {
  label: string;
  score: number;
  note: string;
}

export interface AnalyzerCorrection {
  type: string;
  text: string;
  trend: "up" | "down";
}

export interface AnalyzerReport {
  title: string;
  subtitle?: string;
  composite: number;
  scores: AnalyzerScore[];
  corrections: AnalyzerCorrection[];
  generatedAt?: Date;
  isSample?: boolean;
}

/**
 * Stages emitted by `downloadAnalyzerReportPdf` so the UI can show a
 * deterministic, real-stage progress bar instead of a fake animation.
 * Stage names are stable so tests can assert against them.
 */
export type AnalyzerPdfStage =
  | "init"
  | "header"
  | "composite"
  | "scores"
  | "corrections"
  | "footer"
  | "save"
  | "done"
  | "error";

export interface AnalyzerPdfProgress {
  stage: AnalyzerPdfStage;
  /** 0-100. Always reaches 100 on `done` or `error` so the UI can complete. */
  percent: number;
  /** Human-readable label for the current stage. */
  label: string;
}

const STAGE_PERCENT: Record<AnalyzerPdfStage, number> = {
  init: 5,
  header: 15,
  composite: 30,
  scores: 55,
  corrections: 80,
  footer: 92,
  save: 97,
  done: 100,
  error: 100,
};

const STAGE_LABEL: Record<AnalyzerPdfStage, string> = {
  init: "Preparing document",
  header: "Drawing header",
  composite: "Rendering composite score",
  scores: "Rendering dimension scores",
  corrections: "Rendering corrections",
  footer: "Adding footer",
  save: "Saving PDF",
  done: "Complete",
  error: "Failed",
};

/**
 * Render the Post-Presentation AI Coach Analyzer report as a downloadable
 * PDF. Used for both the landing-page sample and the real after-action
 * report inside Oratory Studio.
 *
 * Pass `onProgress` to receive deterministic stage updates that always end
 * at percent=100 (with stage="done" on success, stage="error" on failure).
 */
export function downloadAnalyzerReportPdf(
  report: AnalyzerReport,
  filename = "analyzer-report.pdf",
  onProgress?: (p: AnalyzerPdfProgress) => void,
) {
  const emit = (stage: AnalyzerPdfStage) => {
    try {
      onProgress?.({ stage, percent: STAGE_PERCENT[stage], label: STAGE_LABEL[stage] });
    } catch {
      /* progress callback must never break PDF generation */
    }
  };
  emit("init");
  try {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  // Header bar
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, pageWidth, 28, "F");
  doc.setTextColor(212, 175, 55); // sovereign gold
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("THE FLOOR · POST-PRESENTATION AI COACH", margin, 18);

  // Title
  y = 64;
  doc.setTextColor(15, 23, 42);
  doc.setFont("times", "bold");
  doc.setFontSize(22);
  doc.text(report.title, margin, y);
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  const stamp = (report.generatedAt ?? new Date()).toISOString();
  const subtitle = report.subtitle ?? (report.isSample ? "Sample report (illustrative)" : "After-action report");
  doc.text(`${subtitle}  ·  Generated ${stamp}`, margin, y);
  y += 26;
  emit("header");

  // Composite score block
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.rect(margin, y, usableWidth, 56);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(120, 90, 20);
  doc.text("COMPOSITE SCORE", margin + 14, y + 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(15, 23, 42);
  doc.text(String(report.composite), margin + 14, y + 46);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(
    `Across ${report.scores.length} dimensions: ${report.scores.map((s) => s.label).join(" · ")}`,
    margin + 90,
    y + 36,
  );
  y += 80;
  emit("composite");

  // Per-dimension scores
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text("Dimension Highlights", margin, y);
  y += 16;

  for (const s of report.scores) {
    if (y > 700) { doc.addPage(); y = margin; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(`${s.label}`, margin, y);
    doc.text(`${s.score}/100`, pageWidth - margin, y, { align: "right" });
    y += 6;
    // Bar
    doc.setFillColor(226, 232, 240);
    doc.rect(margin, y, usableWidth, 6, "F");
    const fill = (s.score / 100) * usableWidth;
    const color: [number, number, number] =
      s.score >= 80 ? [34, 197, 94] : s.score >= 70 ? [212, 175, 55] : [239, 68, 68];
    doc.setFillColor(color[0], color[1], color[2]);
    doc.rect(margin, y, fill, 6, "F");
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    const wrapped = doc.splitTextToSize(s.note, usableWidth);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 12 + 8;
  }
  emit("scores");

  // Corrections
  if (y > 640) { doc.addPage(); y = margin; }
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text("Top Corrections — Apply Before Next Take", margin, y);
  y += 14;

  for (const c of report.corrections) {
    if (y > 740) { doc.addPage(); y = margin; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(120, 90, 20);
    doc.text(`[${c.type.toUpperCase()}]`, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    const wrapped = doc.splitTextToSize(c.text, usableWidth - 70);
    doc.text(wrapped, margin + 70, y);
    y += wrapped.length * 12 + 4;
  }
  emit("corrections");

  // Footer
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `The Floor · Nachrist Impact Sphere · Page ${i} of ${total}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 18,
      { align: "center" },
    );
  }
  emit("footer");

  emit("save");
  doc.save(filename);
  emit("done");
  } catch (err) {
    emit("error");
    throw err;
  }
}